import type { FileMetadata } from '@/types/poc/helia';
import { normalizeFolderPath } from '@/utils/folderPath.utils';

/**
 * Check if user is an owner of the six
 */
function isUserOwner(currentUserId: string, ownerIds: string[]): boolean {
  return ownerIds.includes(currentUserId);
}

/**
 * Check if user can delete a file.
 * User can delete if they are:
 * - A six owner, OR
 * - The original uploader of the file
 */
export function canDeleteFile(
  file: FileMetadata,
  currentUserId: string | undefined,
  ownerIds: string[]
): boolean {
  if (currentUserId === undefined) {
    return false;
  }

  if (isUserOwner(currentUserId, ownerIds)) {
    return true;
  }

  return file.uploadedByUserId === currentUserId;
}

/**
 * Check if user owns the entire subtree of a folder.
 * Returns true if all files and subfolders within the folder path
 * were created/uploaded by the specified user.
 */
function ownsEntireSubtree(
  folderPath: string,
  currentUserId: string,
  allFiles: FileMetadata[],
  allFolders: FileMetadata[]
): boolean {
  const normalizedFolderPath = normalizeFolderPath(folderPath);

  const filesInSubtree = allFiles.filter(
    (file) => normalizeFolderPath(file.path).startsWith(normalizedFolderPath)
  );

  const foldersInSubtree = allFolders.filter((folder) => {
    const normalizedPath = normalizeFolderPath(folder.path);
    return normalizedPath !== normalizedFolderPath && normalizedPath.startsWith(normalizedFolderPath);
  });

  const allFilesOwnedByUser = filesInSubtree.every(
    (file) => file.uploadedByUserId === currentUserId
  );

  const allFoldersOwnedByUser = foldersInSubtree.every(
    (folder) => folder.uploadedByUserId === currentUserId
  );

  return allFilesOwnedByUser && allFoldersOwnedByUser;
}

interface FolderPermissionContext {
  currentUserId: string | undefined;
  ownerIds: string[];
  allFiles: FileMetadata[];
  allFolders: FileMetadata[];
}

/**
 * Check if user can delete a folder.
 * User can delete if they are:
 * - A six owner, OR
 * - The creator of the folder AND they own the entire subtree
 *   (all files and subfolders within were created/uploaded by them)
 */
export function canDeleteFolder(
  folder: FileMetadata,
  context: FolderPermissionContext
): boolean {
  const { currentUserId, ownerIds, allFiles, allFolders } = context;

  if (currentUserId === undefined) {
    return false;
  }

  if (isUserOwner(currentUserId, ownerIds)) {
    return true;
  }

  const isCreator = folder.uploadedByUserId === currentUserId;
  if (!isCreator) {
    return false;
  }

  return ownsEntireSubtree(folder.path, currentUserId, allFiles, allFolders);
}
